import type * as THREE from "three";

export type WeatherKind = "clear" | "cloudy" | "overcast" | "mist" | "drizzle" | "after-rain";
export type TimePreset = "dawn" | "morning" | "noon" | "golden-hour" | "night";
export type QualityLevel = "medium" | "high";

export interface ComfortSettings {
  fov: number;
  headBob: boolean;
  motionBlur: boolean;
  interactionHints: boolean;
}

export interface ExperienceSettings {
  weather: WeatherKind;
  timePreset: TimePreset;
  timeFlow: boolean;
  residents: boolean;
  animals: boolean;
  quality: QualityLevel;
  masterVolume: number;
  comfort: ComfortSettings;
}

export interface PlayerSnapshot {
  position: [number, number, number];
  yaw: number;
  pitch: number;
}

export interface WorldSave {
  version: 2;
  seed: string;
  updatedAt: number;
  player: PlayerSnapshot;
  settings: ExperienceSettings;
}

export const WORLD_RULES_VERSION = "jiangnan-v2";

export interface FrameContext {
  delta: number;
  elapsed: number;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  settings: ExperienceSettings;
  paused: boolean;
}

export interface RuntimeSystem {
  update(context: FrameContext): void;
  dispose(): void;
}

export interface HorizontalPosition {
  x: number;
  z: number;
}

export interface MovementResolutionRequest {
  position: HorizontalPosition;
  displacement: HorizontalPosition;
  radius: number;
  maxStepHeight: number;
  maxWadeDepth: number;
}

export interface MovementResolution {
  position: HorizontalPosition;
  displacement: HorizontalPosition;
  collided: boolean;
}

export interface WorldQuery {
  getHeight(x: number, z: number): number;
  getWaterDepth(x: number, z: number): number;
  getSurfaceKind(x: number, z: number): "stone" | "earth" | "grass" | "water";
  resolveMovement?(request: MovementResolutionRequest): MovementResolution;
  resetAround(position: THREE.Vector3): void;
}

export const DEFAULT_SETTINGS: ExperienceSettings = {
  weather: "after-rain",
  timePreset: "morning",
  timeFlow: true,
  residents: true,
  animals: true,
  quality: "high",
  masterVolume: 0.65,
  comfort: {
    fov: 68,
    headBob: true,
    motionBlur: false,
    interactionHints: true,
  },
};

export const HOME_POSITION = [0, 2.62, 17.1] as const;
export const HOME_PITCH = -0.075;
